/**
 * Solitaire Error Handler
 *
 * Wraps ErrorHandlingService for game-specific error classification.
 * All user-facing messages go through NotificationService — never raw alert().
 *
 * Follows SudokuErrorHandler pattern.
 */

import {
  ErrorHandlingService,
  ErrorType,
  ErrorSeverity,
} from '../../services/ErrorHandlingService';
import {
  NotificationService,
  NotificationType,
} from '../../services/NotificationService';

export enum SolitaireErrorType {
  INVALID_MOVE = 'INVALID_MOVE',
  NO_VALID_TARGET = 'NO_VALID_TARGET',
  GAME_ENDED = 'GAME_ENDED',
  GAME_LOGIC = 'GAME_LOGIC',
  CORRUPTED_DATA = 'CORRUPTED_DATA',
  SAVE_ERROR = 'SAVE_ERROR',
  LOAD_ERROR = 'LOAD_ERROR',
  UNKNOWN = 'UNKNOWN',
}

export interface SolitaireError {
  type: SolitaireErrorType;
  message: string;
  details?: unknown;
  timestamp: number;
  recoverable: boolean;
}

export class SolitaireErrorHandler {
  private errorService: ErrorHandlingService;
  private notificationService: NotificationService;
  private errorLog: SolitaireError[] = [];
  private readonly MAX_ERROR_LOG = 50;

  constructor(
    errorService: ErrorHandlingService,
    notificationService: NotificationService
  ) {
    this.errorService = errorService;
    this.notificationService = notificationService;
  }

  handleError(
    type: SolitaireErrorType,
    message: string,
    details?: unknown,
    showNotification: boolean = true
  ): SolitaireError {
    const error: SolitaireError = {
      type,
      message,
      details,
      timestamp: Date.now(),
      recoverable: this.isRecoverable(type),
    };

    this.logError(error);

    const { errorType, severity } = this.classifyError(type);
    this.errorService.handleError(
      new Error(message),
      errorType,
      severity,
      { component: 'Solitaire', additionalData: details as Record<string, unknown> },
      { showNotification: false }
    );

    if (showNotification) {
      this.showErrorNotification(error);
    }

    return error;
  }

  private classifyError(
    type: SolitaireErrorType
  ): { errorType: ErrorType; severity: ErrorSeverity } {
    switch (type) {
      case SolitaireErrorType.INVALID_MOVE:
      case SolitaireErrorType.NO_VALID_TARGET:
      case SolitaireErrorType.GAME_ENDED:
      case SolitaireErrorType.GAME_LOGIC:
        return { errorType: ErrorType.GAME_LOGIC, severity: ErrorSeverity.LOW };

      case SolitaireErrorType.CORRUPTED_DATA:
      case SolitaireErrorType.SAVE_ERROR:
      case SolitaireErrorType.LOAD_ERROR:
        return { errorType: ErrorType.STORAGE, severity: ErrorSeverity.MEDIUM };

      default:
        return { errorType: ErrorType.SYSTEM, severity: ErrorSeverity.MEDIUM };
    }
  }

  handleInvalidMove(reason: string): SolitaireError {
    return this.handleError(
      SolitaireErrorType.INVALID_MOVE,
      `Invalid move: ${reason}`,
      { reason }
    );
  }

  handleGameLogicError(reason: string): SolitaireError {
    return this.handleError(
      SolitaireErrorType.GAME_LOGIC,
      `Game error: ${reason}`,
      { reason }
    );
  }

  handleCorruptedData(dataType: string, error: unknown): SolitaireError {
    return this.handleError(
      SolitaireErrorType.CORRUPTED_DATA,
      'Unable to load saved game. Starting new game.',
      { dataType, error },
      true
    );
  }

  handleSaveError(gameId: string, error: unknown): SolitaireError {
    return this.handleError(
      SolitaireErrorType.SAVE_ERROR,
      'Failed to save game. Your progress may be lost.',
      { gameId, error },
      true
    );
  }

  handleLoadError(gameId: string, error: unknown): SolitaireError {
    return this.handleError(
      SolitaireErrorType.LOAD_ERROR,
      'Failed to load game. Starting new game.',
      { gameId, error },
      true
    );
  }

  private isRecoverable(type: SolitaireErrorType): boolean {
    switch (type) {
      case SolitaireErrorType.INVALID_MOVE:
      case SolitaireErrorType.NO_VALID_TARGET:
      case SolitaireErrorType.GAME_ENDED:
      case SolitaireErrorType.GAME_LOGIC:
      case SolitaireErrorType.CORRUPTED_DATA:
      case SolitaireErrorType.SAVE_ERROR:
      case SolitaireErrorType.LOAD_ERROR:
        return true;
      default:
        return false;
    }
  }

  private logError(error: SolitaireError): void {
    this.errorLog.push(error);
    if (this.errorLog.length > this.MAX_ERROR_LOG) {
      this.errorLog.shift();
    }
  }

  private showErrorNotification(error: SolitaireError): void {
    const notificationType = error.recoverable
      ? NotificationType.WARNING
      : NotificationType.ERROR;
    this.notificationService.addNotification({
      type: notificationType,
      title: 'Solitaire Error',
      message: error.message,
      duration: 5000,
    });
  }

  getErrorLog(): SolitaireError[] {
    return [...this.errorLog];
  }

  clearErrorLog(): void {
    this.errorLog = [];
  }
}
